import axios from 'axios'

/**
 * API client for the platform-administration panel.
 *
 * Deliberately its own file rather than a copy of Frontend's lib/api.js: that
 * client juggles a tenant session's token AND a platform session's token in
 * one module, because both can be open in the same browser tab. This app has
 * exactly one kind of session — there is no tenant concept here at all — so
 * carrying that dual-token complexity over would be solving a problem this
 * project does not have.
 *
 * The bearer token lives in memory, with a copy in sessionStorage rather than
 * localStorage — scoped to the tab, cleared when it closes, same reasoning as
 * the tenant app: this can be opened on a shared machine.
 */

const TOKEN_KEY = 'gp.platformToken'

let token = sessionStorage.getItem(TOKEN_KEY) || null

let onUnauthorized = () => {}

export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler
}

export function setToken(value) {
  token = value
  if (value) sessionStorage.setItem(TOKEN_KEY, value)
  else sessionStorage.removeItem(TOKEN_KEY)
}

export function getToken() {
  return token
}

// Relative by default — same-origin in dev (via vite.config.js's proxy) and
// in production (see DEPLOYMENT.md for the shared-origin reverse-proxy
// setup). Overridable through VITE_API_BASE_URL for a deploy where this
// panel and the API are not same-origin.
const client = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: { Accept: 'application/json' },
})

client.interceptors.request.use((config) => {
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const hadToken = Boolean(token)
      setToken(null)
      onUnauthorized(hadToken)
    }

    return Promise.reject(normalise(error))
  },
)

/**
 * Turn any failure into one predictable shape, same convention as the tenant
 * app's client — screens render `error.message` and are done with it.
 */
function normalise(error) {
  const response = error.response

  if (!response) {
    return {
      status: 0,
      message: navigator.onLine
        ? 'Could not reach the server. Please try again.'
        : 'You appear to be offline. Check your connection and try again.',
      fields: {},
      offline: !navigator.onLine,
      original: error,
    }
  }

  const data = response.data || {}

  return {
    status: response.status,
    message: data.message || defaultMessage(response.status),
    fields: data.errors && !Array.isArray(data.errors) ? data.errors : {},
    original: error,
  }
}

function defaultMessage(status) {
  switch (status) {
    case 400: return 'That request could not be processed.'
    case 401: return 'Your session has ended. Please sign in again.'
    case 403: return 'You do not have permission to do that.'
    case 404: return 'Not found.'
    case 409: return 'That conflicts with something that already exists.'
    case 422: return 'Please check the highlighted fields.'
    case 429: return 'Too many attempts. Please wait a moment and try again.'
    case 500: return 'Something went wrong at our end. Please try again.'
    default:  return 'Something went wrong. Please try again.'
  }
}

/** Unwraps the `{ success, data }` envelope so callers deal in plain data. */
async function unwrap(promise) {
  const response = await promise
  return response.data?.data !== undefined ? response.data.data : response.data
}

export const api = {
  get:    (url, config) => unwrap(client.get(url, config)),
  post:   (url, body, config) => unwrap(client.post(url, body, config)),
  put:    (url, body, config) => unwrap(client.put(url, body, config)),
  delete: (url, config) => unwrap(client.delete(url, config)),

  /** Full response, for endpoints whose `meta` (pagination) the caller needs. */
  raw: {
    get: (url, config) => client.get(url, config).then((r) => r.data),
  },

  /** File downloads — a tenant backup comes back as a blob, not JSON. */
  async download(url, filename, config = {}) {
    const response = await client.get(url, {
      ...config,
      responseType: 'blob',
      // A full tenant database dump can genuinely take a while to generate
      // for a long-lived tenant — the default 30s timeout is tuned for
      // ordinary JSON calls, not this.
      timeout: 180000,
    })

    const objectUrl = URL.createObjectURL(response.data)
    const link = document.createElement('a')

    link.href = objectUrl
    link.download = filename ?? deriveFilename(response) ?? 'download'
    document.body.appendChild(link)
    link.click()
    link.remove()

    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
  },
}

function deriveFilename(response) {
  const disposition = response.headers['content-disposition']
  if (!disposition) return null

  const match = /filename="?([^"]+)"?/.exec(disposition)
  return match ? match[1] : null
}

export default client
